import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Search, 
  RefreshCw, 
  Eye, 
  Ban, 
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  User
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

const ADMIN_API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/secure-admin`;

export const AdminUsersPage = () => {
  const { token } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [suspendDialog, setSuspendDialog] = useState({ open: false, user: null });
  const [suspendReason, setSuspendReason] = useState('');
  const limit = 20;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });
      if (search) params.append('search', search);

      const response = await fetch(`${ADMIN_API_URL}/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load users');

      const data = await response.json();
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [token, offset]);

  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0);
    fetchUsers();
  };

  const handleViewUser = async (userId) => {
    try {
      const response = await fetch(`${ADMIN_API_URL}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load user details');
      const data = await response.json();
      setSelectedUser(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSuspend = async () => {
    if (suspendReason.length < 10) {
      setError('Suspension reason must be at least 10 characters');
      return;
    }

    try {
      const response = await fetch(
        `${ADMIN_API_URL}/users/${suspendDialog.user.id}/suspend?reason=${encodeURIComponent(suspendReason)}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to suspend user');

      setSuspendDialog({ open: false, user: null });
      setSuspendReason('');
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUnsuspend = async (userId) => {
    try {
      const response = await fetch(`${ADMIN_API_URL}/users/${userId}/unsuspend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to unsuspend user');
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">User Management</h1>
        <Button onClick={fetchUsers} variant="outline" size="sm" className="border-gray-600">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="bg-red-900/50 border-red-800">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email, name, or nickname..."
            className="bg-gray-800 border-gray-700 text-white pl-10"
            data-testid="user-search-input"
          />
        </div>
        <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="user-search-btn">
          Search
        </Button>
      </form>

      {/* Users Table */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Joined</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-gray-300" />
                          </div>
                          <span className="text-white font-medium">
                            {user.name || user.nickname || 'Anonymous'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-300">{user.email}</td>
                      <td className="px-4 py-3">
                        <Badge className="bg-blue-600/20 text-blue-400 border-blue-600 capitalize">
                          {user.role}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {user.is_suspended ? (
                          <Badge className="bg-red-600/20 text-red-400 border-red-600">
                            Suspended
                          </Badge>
                        ) : (
                          <Badge className="bg-green-600/20 text-green-400 border-green-600">
                            Active
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewUser(user.id)}
                            className="text-gray-400 hover:text-white"
                            data-testid={`view-user-${user.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {user.is_suspended ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUnsuspend(user.id)}
                              className="text-green-400 hover:text-green-300"
                              data-testid={`unsuspend-user-${user.id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setSuspendDialog({ open: true, user })}
                              className="text-red-400 hover:text-red-300"
                              data-testid={`suspend-user-${user.id}`}
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
              <p className="text-sm text-gray-400">
                Showing {offset + 1} to {Math.min(offset + limit, total)} of {total} users
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="border-gray-600"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-3 py-1 text-gray-400">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= total}
                  className="border-gray-600"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Detail Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-400">ID</p>
                  <p className="font-mono">{selectedUser.user?.id}</p>
                </div>
                <div>
                  <p className="text-gray-400">Email</p>
                  <p>{selectedUser.user?.email}</p>
                </div>
                <div>
                  <p className="text-gray-400">Name</p>
                  <p>{selectedUser.user?.name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-400">Role</p>
                  <Badge className="bg-blue-600/20 text-blue-400 capitalize">
                    {selectedUser.user?.role}
                  </Badge>
                </div>
              </div>

              {selectedUser.trust_profile && (
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-gray-400 mb-2">Trust Profile</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Trust Score: {selectedUser.trust_profile.score || 0}</div>
                    <div>Flagged: {selectedUser.trust_profile.flagged ? 'Yes' : 'No'}</div>
                  </div>
                </div>
              )}

              <div className="border-t border-gray-700 pt-4">
                <p className="text-gray-400 mb-2">Stats</p>
                <div className="text-sm">
                  Total Unlocks: {selectedUser.stats?.total_unlocks || 0}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialog.open} onOpenChange={(open) => !open && setSuspendDialog({ open: false, user: null })}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400">Suspend User</DialogTitle>
            <DialogDescription className="text-gray-400">
              You are about to suspend {suspendDialog.user?.email}. This action will be logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400">Reason (min 10 characters)</label>
              <Input
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Enter suspension reason..."
                className="bg-gray-700 border-gray-600 text-white mt-1"
                data-testid="suspend-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSuspendDialog({ open: false, user: null })}
              className="border-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSuspend}
              className="bg-red-600 hover:bg-red-700"
              disabled={suspendReason.length < 10}
              data-testid="confirm-suspend-btn"
            >
              Suspend User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
